import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  useId,
} from 'react';
import { cn } from './utils';

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode;
  description?: ReactNode;
  error?: boolean;
}

const inputStyles =
  'w-4 h-4 mt-0.5 accent-accent cursor-pointer border-neutral-300 focus:ring-2 focus:ring-accent disabled:cursor-not-allowed disabled:opacity-50';

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  function Radio(props, ref) {
    const {
      label,
      description,
      error = false,
      className = '',
      id: providedId,
      disabled,
      ...rest
    } = props;

    const generatedId = useId();
    const id = providedId || generatedId;
    const descriptionId = description ? `${id}-description` : undefined;

    const inputClassName = cn(
      inputStyles,
      error && 'ring-2 ring-error-border',
      className,
    );

    if (!label && !description) {
      return (
        <input
          ref={ref}
          type="radio"
          id={id}
          className={inputClassName}
          disabled={disabled}
          aria-invalid={error || undefined}
          {...rest}
        />
      );
    }

    return (
      <div className="flex items-start gap-2">
        <input
          ref={ref}
          type="radio"
          id={id}
          className={inputClassName}
          disabled={disabled}
          aria-describedby={descriptionId}
          aria-invalid={error || undefined}
          {...rest}
        />
        <div className="flex-1">
          {label && (
            <label
              htmlFor={id}
              className={cn(
                'text-sm select-none',
                disabled ? 'text-neutral-400 cursor-not-allowed' : 'text-neutral-800 cursor-pointer',
              )}
            >
              {label}
            </label>
          )}
          {description && (
            <p id={descriptionId} className="text-xs text-neutral-500 mt-0.5">
              {description}
            </p>
          )}
        </div>
      </div>
    );
  },
);

export interface RadioOption {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export type RadioGroupOrientation = 'vertical' | 'horizontal';

export interface RadioGroupProps {
  name: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  options: RadioOption[];
  orientation?: RadioGroupOrientation;
  disabled?: boolean;
  error?: boolean;
  label?: ReactNode;
  helpText?: ReactNode;
  className?: string;
}

export function RadioGroup(props: RadioGroupProps) {
  const {
    name,
    value,
    defaultValue,
    onChange,
    options,
    orientation = 'vertical',
    disabled,
    error,
    label,
    helpText,
    className = '',
  } = props;

  const groupId = useId();
  const helpTextId = helpText ? `${groupId}-help` : undefined;
  const labelId = label ? `${groupId}-label` : undefined;
  const containerClass =
    orientation === 'horizontal' ? 'flex flex-wrap gap-4' : 'flex flex-col gap-2';

  const classes = cn(className);

  return (
    <div className={classes}>
      {label && (
        <div id={labelId} className="block text-sm font-medium text-neutral-600 mb-2">
          {label}
        </div>
      )}
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        aria-describedby={helpTextId}
        aria-invalid={error || undefined}
        className={containerClass}
      >
        {options.map((option) => (
          <Radio
            key={option.value}
            name={name}
            value={option.value}
            label={option.label}
            description={option.description}
            disabled={disabled || option.disabled}
            error={error}
            checked={value !== undefined ? value === option.value : undefined}
            defaultChecked={
              value === undefined && defaultValue !== undefined
                ? defaultValue === option.value
                : undefined
            }
            onChange={(e) => {
              if (e.target.checked) onChange?.(option.value);
            }}
          />
        ))}
      </div>
      {helpText && (
        <p id={helpTextId} className="text-xs text-neutral-500 mt-1">
          {helpText}
        </p>
      )}
    </div>
  );
}
